import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { HourlyConfigColumn } from '@model';

@Component({
  selector: 'bmd-hourly-config-modal',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./hourly-config-modal.component.css'],
  templateUrl: './hourly-config-modal.component.html',
})
export class HourlyConfigModalComponent implements OnInit {
  @Input() fieldsToUpdate: HourlyConfigColumn<number[]>[];
  @Input() showError = false;
  @Input() headerText: string;
  @Input() subHeaderText: string;
  @Input() requireFields: boolean;
  @Output() savedConfig = new EventEmitter<any>();

  saveDialogMessage = 'An error was detected while saving!';
  saveDialogColor = 'text-danger';
  saveDialogIcon = 'bi-exclamation-triangle';

  dynamicForm: FormGroup;
  formArray: FormArray;
  fieldNames: string[] = [];

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<HourlyConfigModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MatDialogConfig,
  ) {
    this.formBuilder.group({
      theForm: [],
    });
  }

  ngOnInit() {
    // Default setup if never setup before
    // create a form array for the group
    this.formArray = new FormArray([]);

    // turn any strings to arrays
    this.fieldsToUpdate.forEach(field => {
      if (!Array.isArray(field.value)) {
        field.value = this.maybeJsonParse(field.value);
      }
    });

    for (let hourNumber = 0; hourNumber < 25; hourNumber++) {
      const group = this.formBuilder.group({});
      this.fieldsToUpdate.forEach(field => {
        const formControl = new FormControl(field.value[hourNumber], []);
        if (this.requireFields) {
          formControl.setValidators([Validators.required, Validators.min(0)]);
        }
        group.addControl(`${field.columnName}`, formControl);
      });

      this.formArray.push(group);
    }

    // create a top-level form
    this.dynamicForm = this.formBuilder.group({
      theForm: this.formArray,
    });
  }

  isNumber(numStr: string): boolean {
    return !isNaN(parseFloat(numStr)) && !isNaN(+numStr);
  }

  processClipboard() {
    navigator.clipboard
      .readText()
      .then(text => {
        const row_data = text.split(/\r\n|\r|\n/);

        // Analyze the selected text to see if it comes from a AS Price sheet
        // Expected format:
        // Row 0 header (optional)
        // Row 1..N: HEx\t{number}\t{number}\t{number}\t{number} OR {number}\t{number}\t{number}\t{number}
        let index = 0;

        row_data.forEach(row_Data => {
          const columns = row_Data.split('\t');

          // Skip columns until we encounter a number
          let startIndex = 0;
          let controlIndex = index;
          let hasHE = false;

          // Check if the previous column (if any) is an HE column >> in this case, use it as an index positioning
          if (columns[0].startsWith('HE 2*') || columns[0].startsWith('HE2*')) {
            controlIndex = 24;
            hasHE = true;
          } else if (columns[0].startsWith('HE')) {
            hasHE = true;
            const possibleIndex = parseInt(columns[startIndex].substring(2));
            if (!isNaN(possibleIndex)) {
              controlIndex = possibleIndex - 1;
            } else {
              // Cannot determine, better not screw up the grid, abort this row
              return;
            }
          }

          if (hasHE) {
            startIndex = 1;
          }

          // Check boundaries of found index
          if (controlIndex >= 0 && controlIndex < 25) {
            // Grab the index-th row of controls
            const formGroup: FormGroup = this.formArray.controls[controlIndex] as FormGroup;

            // Paste the values
            let fieldIndex = 0;
            for (const field of this.fieldsToUpdate) {
              formGroup.get(field.columnName).setValue(columns[startIndex + fieldIndex]);
              fieldIndex++;
            }
          }

          ++index;
        });
      })
      .catch(error => {
        console.error('Cannot read clipboard text: ', error);
      });
  }

  get theForm() {
    return this.dynamicForm?.get('theForm') as FormArray;
  }

  cloneRowEverywhere(index) {
    this.cloneRow(index, 0, 24);
  }

  cloneRowUp(index) {
    this.cloneRow(index, 0, index - 1);
  }

  cloneRowDown(index) {
    this.cloneRow(index, index + 1, 24);
  }

  cloneRow(sourceIndex: number, start: number, end: number) {
    const formGroup: FormGroup = this.formArray.controls[sourceIndex] as FormGroup;
    let index = 0;

    this.formArray.controls.forEach(fg => {
      if (index >= start && index <= end) {
        fg.patchValue(formGroup.value);
      }

      index++;
    });
  }

  updateEntity() {
    if (!window.confirm(`Are you sure you want to update?`)) {
      return;
    }

    // Update the grid
    for (let hourNumber = 0; hourNumber < 25; hourNumber++) {
      const formGroup: FormGroup = this.formArray.controls[hourNumber] as FormGroup;
      this.fieldsToUpdate.forEach(field => {
        field.value[hourNumber] = formGroup.get(field.columnName).value;
      });
    }

    const config = this.fieldsToUpdate.reduce((newConfig, field: HourlyConfigColumn<number[]>) => {
      newConfig[field.originalPropertyName] = field.value;
      return newConfig;
    }, {} as unknown);
    this.savedConfig.emit(config);

    // Persist the data
    this.saveDialogMessage = 'Saving data, please wait...';
    this.saveDialogColor = 'text-primary';
    this.saveDialogIcon = 'bi-hourglass-split';
  }

  cancelForm() {
    if (!window.confirm(`Are you sure you want to cancel the changes?`)) {
      return;
    }
    this.savedConfig.emit(null);
    this.dialogRef.close('CANCEL');
  }

  maybeJsonParse(input: any) {
    try {
      return JSON.parse(input);
    } catch {
      return input;
    }
  }
}
